//External libs
import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Subscription } from 'rxjs';

//Local libs
import Service from 'src/app/common/service';
import WaterTypeModel from './waterType.model';

/**
 * @service WaterTypeService
 * @description Service for managing Water Type definitions within the aquaculture system.
 * Water Types categorize the source and salinity profile of the water used in ponds.
 *
 * Common examples include:
 * - **Fresh Water**: Low salinity (< 0.5 ppt).
 * - **Brackish Water**: Mixed fresh and salt water (0.5 - 30 ppt).
 * - **Marine/Salt Water**: High salinity (> 30 ppt).
 *
 * This classification is critical for:
 * 1. **Species Selection**: Determining which species can be cultured (e.g., Tilapia vs. Shrimp).
 * 2. **Parameter Management**: Setting appropriate ranges for water quality parameters like Salinity and Alkalinity.
 * 3. **Resource Planning**: Managing water intake and treatment strategies.
 *
 * The service interacts with the `/aqu/watertype/` backend endpoint.
 *
 * @feature 1. Retrieve all defined Water Types.
 * @feature 2. Support for large datasets (pageSize=2000) to ensure all types are loaded.
 * @feature 3. Robust management of concurrent HTTP requests to prevent memory leaks.
 *
 * @architecture
 * Extends base Service<WaterTypeModel> providing REST API integration.
 * Uses HttpClient for HTTP operations and manages subscriptions for cleanup.
 *
 * @service-endpoint /aqu/watertype/
 * @service-provider root (singleton, application-wide)
 *
 * @author AquaCloud Development Team
 * @version 1.0.0
 */
@Injectable({
  providedIn: 'root'
})
export class WaterTypeService extends Service<WaterTypeModel> {

  /**
   * @type {Array<Subscription>}
   * @description Array tracking all ongoing HTTP request subscriptions.
   * Used to maintain references to active Observables so they can be unsubscribed
   * when the service is destroyed or when cancellation is requested.
   * @default []
   * @side-effect Used to store subscription references for cancellation via cancelOnGoingHttpRequests()
   */
  public ongoingRequests: Subscription[] = [];

  /**
   * @method constructor
   * @description Service constructor with dependency injection of HttpClient.
   *
   * Initializes the service by calling the parent Service constructor with the
   * HttpClient instance and the specific API endpoint path (`/aqu/watertype/`).
   *
   * @param {HttpClient} httpClient - @service Angular HTTP client for making HTTP requests to the backend.
   * @returns {void}
   */
  constructor(public httpClient: HttpClient) {
    super(httpClient, '/blubay/watertype/');
  }

  /**
   * @method getAllWaterTypes
   * @async
   * @description Fetch all Water Type records from the backend.
   *
   * This method performs a GET request to the service endpoint. It is configured to retrieve
   * a large number of records (pageSize=2000) to ensure all types are loaded,
   * ordered by creation date ('new').
   *
   * @returns {Promise<Array<WaterTypeModel>>} Promise resolving to an array of WaterTypeModel objects.
   * @throws {HttpErrorResponse} Rejects the promise if the HTTP request fails.
   * @side-effect
   * - Initiates an HTTP GET request.
   * - Pushes the subscription to `ongoingRequests`.
   *
   * @example
   * waterTypeService.getAllWaterTypes().then(types => {
   *   console.log('Water Types:', types);
   * });
   */
  public getAllWaterTypes() {
    return new Promise<Array<WaterTypeModel>>((resolve, reject) => {
      const request = this.httpClient.get<Array<WaterTypeModel>>(`${this.baseUrl}${this.serviceUrl}?pageSize=2000&order=new`, this.httpOptions).subscribe(response => {
        resolve(response);
      }, (error: HttpErrorResponse) => {
        reject(error);
      });
      this.ongoingRequests.push(request);
    });
  }

  /**
   * @method cancelOnGoingHttpRequests
   * @description Cancel all ongoing/pending HTTP requests to prevent memory leaks.
   *
   * This method iterates through the `ongoingRequests` array and calls `unsubscribe()`
   * on each subscription. This is typically called when a component using this service
   * is destroyed (ngOnDestroy) to ensure that no network requests continue in the background.
   *
   * @returns {void}
   * @side-effect
   * - Aborts all pending HTTP requests tracked in `ongoingRequests`.
   * - Resets `ongoingRequests` to an empty array.
   */
  public cancelOnGoingHttpRequests(): void {
    this.ongoingRequests.forEach((request) => {
      if (request && typeof request.unsubscribe === 'function') {
        request.unsubscribe();
      }
    });
    this.ongoingRequests = [];
  }
}
